"use client";

import { Tag, Text } from "@pamperedchef/design-system";
import * as styles from "./PriceDisplay.module.scss";

const PRICE_TYPOGRAPHY: Record<
	NonNullable<PriceDisplayProps["size"]>,
	{ current: string; strike: string; value: string }
> = {
	large: {
		current: "typography-price-lg",
		strike: "typography-price-sm-strikethrough",
		value: "typography-price-sm"
	},
	small: {
		current: "typography-price-md",
		strike: "typography-price-xs-strikethrough",
		value: "typography-price-xs"
	},
	compact: {
		current: "typography-price-sm",
		strike: "typography-price-xs-strikethrough",
		value: "typography-price-xs"
	}
};

function formatPrice(value: number, locale: string, currency: string): string {
	return new Intl.NumberFormat(locale, {
		style: "currency",
		currency
	}).format(value);
}

export interface PriceDisplayProps {
	display?: number;
	strikeThrough?: number;
	productValue?: { value?: number; label?: string };
	currency?: string;
	locale?: string;
	/** Renders a purple "Half-Price Combo" pill next to the price. */
	halfPriceCombo?: boolean;
	/** Renders a green "Kit Credit" pill next to the price. */
	kitCredit?: boolean;
	size?: "small" | "large" | "compact";
	className?: string;
}

export function PriceDisplay({
	display,
	strikeThrough,
	productValue,
	currency = "USD",
	locale = "en-US",
	halfPriceCombo,
	kitCredit,
	size = "small",
	className
}: PriceDisplayProps) {
	const typography = PRICE_TYPOGRAPHY[size];

	return (
		<div
			className={[styles["price"], className ?? ""]
				.filter(Boolean)
				.join(" ")}
			data-component="PriceDisplay"
			data-size={size}
		>
			{display !== undefined ? (
				<Text
					tag="span"
					data-part="current"
					className={`${styles["price__current"]} ${typography.current}`}
				>
					{formatPrice(display, locale, currency)}
				</Text>
			) : null}
			{strikeThrough !== undefined ? (
				<Text
					tag="span"
					data-part="strike"
					className={`${styles["price__muted"]} ${typography.strike}`}
				>
					{formatPrice(strikeThrough, locale, currency)}
				</Text>
			) : null}
			{productValue?.value !== undefined ? (
				<Text
					tag="span"
					data-part="value"
					className={`${styles["price__muted"]} ${typography.value}`}
				>
					{`(${formatPrice(productValue.value, locale, currency)}${
						productValue.label ? ` ${productValue.label}` : ""
					})`}
				</Text>
			) : null}
			{halfPriceCombo ? (
				<Tag
					label="Half-Price Combo"
					kind="solid"
					size="md"
					color="purple"
				/>
			) : null}
			{kitCredit ? (
				<Tag
					label="Kit Credit"
					kind="solid"
					size="md"
					color="emerald"
				/>
			) : null}
		</div>
	);
}
